import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Box,
  Divider,
  Chip
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { UserDeletionValidation } from '../../../services/user/user.service';

export interface UserDeletionValidationDialogProps {
  open: boolean;
  onClose: () => void;
  validation: UserDeletionValidation | null;
  onProceedWithDeletion?: () => void;
  loading?: boolean;
}

const UserDeletionValidationDialog: React.FC<UserDeletionValidationDialogProps> = ({
  open,
  onClose,
  validation,
  onProceedWithDeletion,
  loading = false
}) => {
  if (!validation) return null;

  const getEntityIcon = (type: 'organization' | 'team') => {
    return type === 'organization' ? <BusinessIcon /> : <GroupIcon />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {validation.canDelete ? (
            <>
              <WarningIcon color="warning" />
              Confirm Account Deletion
            </>
          ) : (
            <>
              <ErrorIcon color="error" />
              Cannot Delete Account
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Blockers Section (if any) */}
        {!validation.canDelete && validation.blockers.length > 0 && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              You cannot delete your account because you are the last administrator of the following:
            </Alert>

            <List>
              {validation.blockers.map((blocker, index) => (
                <ListItem
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: 'error.light',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'error.lighter'
                  }}
                >
                  <ListItemIcon>
                    {getEntityIcon(blocker.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2" component="span">
                          {blocker.entityName}
                        </Typography>
                        <Chip
                          label={blocker.type}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary">
                          Role: {blocker.role}
                        </Typography>
                        <br />
                        <Typography component="span" variant="body2" color="error">
                          {blocker.reason}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
              To delete your account, you must:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="1. Add another administrator to each organization/team listed above, OR"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="2. Delete the organization/team if you no longer need it"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Organizations and teams must always have at least one administrator.
            </Typography>
          </>
        )}

        {/* Can Delete - Show Warnings */}
        {validation.canDelete && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                This action cannot be undone!
              </Typography>
              <Typography variant="body2">
                Deleting your account will permanently remove all your data and you will lose access to all organizations and teams.
              </Typography>
            </Alert>

            {/* Dependent Entities Summary */}
            {validation.dependentEntities && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  What will be affected:
                </Typography>
                <List dense>
                  {validation.dependentEntities.organizations > 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <BusinessIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`You will leave ${validation.dependentEntities.organizations} organization(s)`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                  {validation.dependentEntities.teams > 0 && (
                    <ListItem>
                      <ListItemIcon>
                        <GroupIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`You will leave ${validation.dependentEntities.teams} team(s)`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {validation.canDelete ? 'Cancel' : 'Understood'}
        </Button>
        {validation.canDelete && onProceedWithDeletion && (
          <Button
            onClick={onProceedWithDeletion}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Yes, Delete My Account'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserDeletionValidationDialog;
